import { CurrencyPipe, NgForOf, NgIf } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { AlertFacade } from '@curbnturf/alert';
import { TrailerSvgLineComponent } from '@curbnturf/custom-svgs';
import { HelperFunctions } from '@curbnturf/entities';
import { CheckedComponent } from '@curbnturf/form-helpers';
import { listingComplete } from '@curbnturf/helpers';
import { ListingFacade, ListingService } from '@curbnturf/listing';
import { Logger } from '@curbnturf/network';
import { IListing, ISite } from '@curbnturf/objects';
import { SiteService } from '@curbnturf/site';
import { AlertController, IonicModule, LoadingController, ModalController } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { EditSiteNameModalComponent } from '../edit-site-name-modal/edit-site-name-modal.component';
import { EditSitePhotosModalComponent } from '../edit-site-photos-modal/edit-site-photos-modal.component';

@Component({
  selector: 'curbnturf-edit-sites',
  templateUrl: './edit-sites.component.html',
  styleUrls: ['./edit-sites.component.scss'],
  standalone: true,
  imports: [
    CheckedComponent,
    FormsModule,
    IonicModule,
    NgIf,
    ReactiveFormsModule,
    CurrencyPipe,
    TrailerSvgLineComponent,
    NgForOf,
  ],
})
export class EditSitesComponent implements OnDestroy, OnInit {
  siteCompletePercentage = HelperFunctions.siteCompletePercentage;

  @Input() listing: IListing;
  @Output() siteChanged: EventEmitter<{ siteId: number; section: string }> = new EventEmitter();

  form: FormGroup<{
    multisite: FormControl<string | null>;
  }>;

  listingComplete = listingComplete;

  get sites(): ISite[] {
    if (this.listing.sites) {
      return this.listing.sites;
    }
    return [];
  }

  private subscriptions: Subscription[] = [];

  constructor(
    private alertController: AlertController,
    private siteService: SiteService,
    private alertFacade: AlertFacade,
    private listingService: ListingService,
    private logger: Logger,
    private modalController: ModalController,
    private listingFacade: ListingFacade,
    private loadingController: LoadingController,
  ) {}

  ngOnInit() {
    this.form = new FormGroup({
      multisite: new FormControl('', Validators.required),
    });

    this.subscriptions.push(
      this.form.valueChanges.subscribe(async (value) => {
        if (value.multisite === 'single' && this.listing.sites && this.listing.sites.length > 1) {
          const notice = await this.alertController.create({
            header: 'Convert to Single Site?',
            message:
              'Would you like to convert this listing to a single site listing? This will remove all sites except for the first available site.\n\nSITES WILL BE REMOVED. Continue?',
            buttons: [
              { role: 'cancel', text: 'Cancel' },
              {
                role: 'destructive',
                text: 'Yes, Convert to Single Site',
                handler: () => {},
              },
            ],
          });

          await notice.present();
        } else if (value.multisite === 'multi' && this.listing.sites && this.listing.sites.length <= 1) {
          const notice = await this.alertController.create({
            header: 'Convert to Multi Site?',
            message:
              'Would you like to convert this listing to a multi-site listing? This will allow you to add multiple locations for guests to stay at the same time.\n\nContinue?',
            buttons: [
              { role: 'cancel', text: 'Cancel' },
              {
                role: 'destructive',
                text: 'Yes, Convert to Multi-Site',
                handler: () => {},
              },
            ],
          });

          await notice.present();
        }
      }),
    );

    if (this.listing.sites) {
      if (this.listing.sites.length === 1) {
        this.form.patchValue({
          multisite: 'single',
        });
      } else if (this.listing.sites.length > 1) {
        this.form.patchValue({
          multisite: 'multi',
        });
      }
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  async onDeleteSite(site: ISite) {
    if (site.id) {
      const alert = await this.alertController.create({
        message: 'Are you sure you want to delete this site?',
        header: 'Confirm Delete Site',
        buttons: [
          {
            text: 'No',
            role: 'cancel',
          },
          {
            text: 'Yes, Delete',
            role: 'destructive',
            handler: async () => {
              if (site.id) {
                const loading = await this.loadingController.create({
                  message: 'Deleting site...',
                  duration: 10000,
                  backdropDismiss: true,
                });

                await loading.present();

                this.subscriptions.push(
                  this.siteService.remove(site.id).subscribe({
                    next: () => {
                      const siteIndex = this.listing.sites?.findIndex((findSite) => findSite.id === site.id);

                      if (siteIndex || siteIndex === 0) {
                        this.listing.sites?.splice(siteIndex, 1);
                      }
                    },
                    error: () => {
                      this.alertFacade.display({
                        title: 'Error Deleting Site',
                        body: 'There was an error deleting the site. If the problem persists, please contact support.',
                        level: 'error',
                      });
                    },
                    complete: async () => {
                      await loading.dismiss();
                      if (this.listing.id) {
                        this.listingFacade.load(this.listing.id);
                      }
                    },
                  }),
                );
              }
            },
          },
        ],
      });
      await alert.present();
    }
  }

  async onEditSiteName(site: ISite) {
    const modal = await this.modalController.create({
      component: EditSiteNameModalComponent,
      componentProps: {
        site: { ...site, listing: this.listing },
      },
    });

    await modal.present();

    modal.onDidDismiss().then(() => {
      this.refreshSiteList();
    });
  }

  refreshSiteList() {
    if (this.listing.id) {
      this.listingFacade.load(this.listing.id);
    }
  }

  async onEditSite(site: ISite) {
    if (site.id) {
      this.siteChanged.emit({ siteId: site.id, section: 'overview' });
    } else {
      this.logger.error('Cannot edit a site that does not have an identifier.', { site });
      this.alertFacade.display({
        title: 'Error Editing Site',
        body: 'Cannot edit a site that does not have an identifier.',
        level: 'error',
      });
    }
  }

  async onToggleSiteEnable(site: ISite) {
    if (site.available) {
      const alert = await this.alertController.create({
        header: `Disable Site "${site.name}"?`,
        message: `Are you sure you want to disable the site "${site.name}"?`,
        buttons: [
          {
            role: 'cancel',
            text: 'Cancel',
          },
          {
            role: 'destructive',
            text: 'Yes, Disable Site',
            handler: () => {
              this.subscriptions.push(
                this.siteService.disable(site).subscribe(async (results) => {
                  if (results.success) {
                    const foundSite = this.listing.sites?.find((findSite) => findSite.id === site.id);

                    if (foundSite) {
                      foundSite.available = false;
                    }
                  } else {
                    const errorAlert = await this.alertController.create({
                      header: `Error Disabling Site "${site.name}"?`,
                      message: `There was an error: "${results.message}"?`,
                    });

                    await errorAlert.present();
                  }
                }),
              );
            },
          },
        ],
      });

      await alert.present();
    } else {
      const alert = await this.alertController.create({
        header: `Enable Site "${site.name}"?`,
        message: `Are you sure you want to enable the site "${site.name}"?`,
        buttons: [
          {
            role: 'cancel',
            text: 'Cancel',
          },
          {
            role: 'destructive',
            text: 'Yes, Enable Site',
            handler: () => {
              this.subscriptions.push(
                this.siteService.enable(site).subscribe(async (results) => {
                  if (results.success) {
                    const foundSite = this.listing.sites?.find((findSite) => findSite.id === site.id);

                    if (foundSite) {
                      foundSite.available = true;
                    }
                  } else {
                    const errorAlert = await this.alertController.create({
                      header: `Error Enabling Site "${site.name}"?`,
                      message: `There was an error: "${results.message}"?`,
                    });

                    await errorAlert.present();
                  }
                }),
              );
            },
          },
        ],
      });

      await alert.present();
    }
  }

  async onDuplicateSite(site: ISite) {
    const alert = await this.alertController.create({
      header: `Duplicate site "${site.name}"?`,
      message: `You are creating a duplicate of the site "${site.name}". This will copy all of the setting from this site to your new site, except for the images. Please enter a name for the new site.`,
      inputs: [
        {
          name: 'siteName',
          placeholder: 'Site Name',
          min: 2,
        },
      ],
      buttons: [
        {
          role: 'cancel',
          text: 'Cancel',
        },
        {
          role: 'submit',
          text: 'Duplicate Site',
          handler: async (value) => {
            if (site.id) {
              this.subscriptions.push(
                this.siteService
                  .duplicateSite(site.id, value.siteName)
                  .subscribe((newSite) => this.listing.sites?.push(newSite)),
              );
            } else {
              this.alertFacade.display({
                title: 'Error Duplicating Site',
                body: 'Cannot duplicate a site that does not have an identifier.',
                level: 'error',
              });
            }
            await alert.dismiss();
          },
        },
      ],
    });

    await alert.present();
  }

  async onAddSite() {
    if (this.listing.id) {
      const loading = await this.loadingController.create({
        message: 'Adding new site...',
        duration: 10000,
        backdropDismiss: true,
      });

      await loading.present();

      this.subscriptions.push(
        this.listingService.addNewSites(this.listing.id, 1).subscribe({
          next: (listing) => {
            if (this.listing.id) {
              this.listing.sites = listing.sites;
              this.listingFacade.load(this.listing.id);
            }
          },
          error: () => {
            this.alertFacade.display({
              title: 'Error Adding Site',
              body: 'There was an error adding the new site. If the problem persists, please contact support.',
              level: 'error',
            });
          },
          complete: async () => {
            await loading.dismiss();
          },
        }),
      );
    } else {
      this.alertFacade.display({
        title: 'Error Adding Site',
        body: 'Cannot add a site to a listing that does not have an identifier.',
        level: 'error',
      });
    }
  }

  async onUploadSitePhotos(site: ISite) {
    const imageModal = await this.modalController.create({
      component: EditSitePhotosModalComponent,
      componentProps: {
        site,
      },
    });

    await imageModal.present();

    imageModal.onDidDismiss().then(() => {
      this.refreshSite(site);
    });
  }

  refreshSite(site: ISite) {
    if (site.id) {
      this.siteService.fetch(site.id).subscribe((site) => {
        if (this.listing.sites && Array.isArray(this.listing.sites)) {
          const foundSite = this.listing.sites.findIndex((findSite) => findSite.id === site.id);
          if (foundSite !== -1) {
            this.listing.sites?.splice(foundSite, 1, site);
          } else {
            this.listing.sites?.push(site);
          }
        } else {
          this.listing.sites = [site];
        }
      });
    }
  }
}
